import React from "react";

import { faSquare } from "@fortawesome/pro-solid-svg-icons/faSquare";
import { faSquareCheck } from "@fortawesome/pro-solid-svg-icons/faSquareCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover, { PopoverProps } from "antd/es/popover";
import cn from "classnames";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import { ColorCircle } from "@mapmycustomers/ui";

import CustomActivityTypeColor from "@app/enum/CustomActivityTypeColor";
import colors from "@app/styles/_colors.module.scss";
import getClosestColor from "@app/util/colors/getClosestColor";

import styles from "./ActivityTypeColorDropdown.module.scss";

const customColors = [undefined, ...Object.values(CustomActivityTypeColor)];

interface ActivityTypeColorDropdownProps extends Omit<PopoverProps, "content"> {
  colorCircleClassName?: string;
  onChange?: (color: AnyColor | undefined) => void;
  onOpacityChange?: (opacity: number) => void;
  opacity?: number;
  placement?: PopoverProps["placement"];
  value?: AnyColor;
}

const ActivityTypeColorDropdown: React.FC<ActivityTypeColorDropdownProps> = ({
  colorCircleClassName,
  onChange,
  onOpacityChange,
  opacity,
  placement,
  value,
  ...props
}) => {
  let activeColor =
    value && Object.values(CustomActivityTypeColor).includes(anyColorToHex(value))
      ? anyColorToHex(value)
      : undefined;

  if (!activeColor && value) {
    const closestColor = getClosestColor(
      Object.values(CustomActivityTypeColor),
      value,
      undefined,
      50
    );
    if (closestColor) {
      activeColor = anyColorToHex(closestColor);
    }
  }

  return (
    <Popover
      align={{
        offset: [-16, 0],
      }}
      content={
        <div className={styles.panel}>
          {customColors.map((color) => (
            <div
              className={cn(styles.item, { [styles.selected]: color === activeColor })}
              key={color ?? "default"}
              onClick={() => onChange?.(color)}
            >
              <FontAwesomeIcon
                icon={color === activeColor ? faSquareCheck : faSquare}
                style={{ color: color ?? colors.softSlate }}
              />
            </div>
          ))}
        </div>
      }
      destroyTooltipOnHide
      overlayClassName={styles.overlay}
      placement={placement ?? "bottomLeft"}
      trigger={["click"]}
      {...props}
    >
      <div className={styles.circle}>
        <ColorCircle value={value ?? colors.softSlate} />
      </div>
    </Popover>
  );
};

export default ActivityTypeColorDropdown;
